import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Inbox, ChevronLeft, ChevronRight } from 'lucide-react'

interface Column<T> {
    header: string | React.ReactNode
    cell: (item: T) => React.ReactNode
    className?: string
}

interface PremiumTableProps<T> {
    data: T[]
    columns: Column<T>[]
    title?: string
    description?: string
    searchable?: boolean // Placeholder for future search impl
    onRowClick?: (item: T) => void
    emptyMessage?: string
    className?: string
    headerActions?: React.ReactNode
    isLoading?: boolean
    pagination?: {
        currentPage: number
        totalPages: number
        onPageChange: (page: number) => void
    }
}

export function PremiumTable<T>({
    data,
    columns,
    title,
    description,
    onRowClick,
    emptyMessage = "No data available",
    className,
    headerActions,
    isLoading = false,
    pagination
}: PremiumTableProps<T>) {

    const Content = (
        <div className={cn("w-full", className)}>
            {/* Header Section (if not wrapped in Card, or inside Card content) */}
            {(title || headerActions) && !description && (
                <div className="flex items-center justify-between mb-4">
                    {title && <h3 className="text-lg font-semibold">{title}</h3>}
                    {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
                </div>
            )}

            <div className="rounded-md border overflow-hidden">
                <Table>
                    <TableHeader className="bg-muted/30">
                        <TableRow className="hover:bg-transparent border-b-primary/10">
                            {columns.map((col, index) => (
                                <TableHead
                                    key={index}
                                    className={cn("h-12 px-6 text-xs font-medium uppercase tracking-wider text-muted-foreground/80", col.className)}
                                >
                                    {col.header}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <TableRow key={i}>
                                    {columns.map((_, j) => (
                                        <TableCell key={j} className="px-6 py-4">
                                            <div className="h-4 w-full bg-muted/30 animate-pulse rounded" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : data.length > 0 ? (
                            data.map((item, rowIndex) => (
                                <TableRow
                                    key={rowIndex}
                                    className={cn(
                                        "transition-colors hover:bg-muted/40 border-b-muted/50",
                                        onRowClick && "cursor-pointer active:bg-muted/60"
                                    )}
                                    role={onRowClick ? 'button' : undefined}
                                    tabIndex={onRowClick ? 0 : undefined}
                                    aria-label={onRowClick ? 'View details' : undefined}
                                    onKeyDown={onRowClick ? (event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault()
                                            onRowClick(item)
                                        }
                                    } : undefined}
                                    onClick={() => onRowClick?.(item)}
                                >
                                    {columns.map((col, colIndex) => (
                                        <TableCell key={colIndex} className="px-6 py-4">
                                            {col.cell(item)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-48 text-center">
                                    <div className="flex flex-col items-center justify-center text-muted-foreground space-y-2">
                                        <Inbox className="w-8 h-8 opacity-50" />
                                        <p>{emptyMessage}</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {pagination && (
                <div className="flex items-center justify-end space-x-2 py-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage <= 1}
                    >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="sr-only">Previous</span>
                    </Button>
                    <div className="text-sm text-muted-foreground">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage >= pagination.totalPages}
                    >
                        <ChevronRight className="h-4 w-4" />
                        <span className="sr-only">Next</span>
                    </Button>
                </div>
            )}
        </div>
    )

    // If title/description provided, wrap in Card for consistency
    if (title && description) {
        return (
            <Card className="border-none shadow-sm bg-card/50">
                <CardHeader className="px-6 pt-6 pb-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <CardTitle className="text-xl font-semibold tracking-tight">{title}</CardTitle>
                            <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
                        </div>
                        {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
                    </div>
                </CardHeader>
                <CardContent className="p-0 pb-6">
                    {/* Render table without extra header since CardHeader covers it */}
                    <div className="rounded-none border-x-0 border-b-0">
                        <Table>
                            <TableHeader className="bg-muted/30">
                                <TableRow className="hover:bg-transparent border-b-primary/10">
                                    {columns.map((col, index) => (
                                        <TableHead
                                            key={index}
                                            className={cn("h-12 px-6 text-xs font-medium uppercase tracking-wider text-muted-foreground/80", col.className)}
                                        >
                                            {col.header}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <TableRow key={i}>
                                            {columns.map((_, j) => (
                                                <TableCell key={j} className="px-6 py-4">
                                                    <div className="h-4 w-full bg-muted/30 animate-pulse rounded" />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : data.length > 0 ? (
                                    data.map((item, rowIndex) => (
                                        <TableRow
                                            key={rowIndex}
                                            className={cn(
                                                "transition-colors hover:bg-muted/40 border-b-muted/50",
                                                onRowClick && "cursor-pointer active:bg-muted/60"
                                            )}
                                            role={onRowClick ? 'button' : undefined}
                                            tabIndex={onRowClick ? 0 : undefined}
                                            aria-label={onRowClick ? 'View details' : undefined}
                                            onKeyDown={onRowClick ? (event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault()
                                                    onRowClick(item)
                                                }
                                            } : undefined}
                                            onClick={() => onRowClick?.(item)}
                                        >
                                            {columns.map((col, colIndex) => (
                                                <TableCell key={colIndex} className="px-6 py-4">
                                                    {col.cell(item)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-48 text-center">
                                            <div className="flex flex-col items-center justify-center text-muted-foreground space-y-2">
                                                <Inbox className="w-8 h-8 opacity-50" />
                                                <p>{emptyMessage}</p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    {pagination && (
                        <div className="flex items-center justify-end space-x-2 pt-4 px-6">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage <= 1}
                            >
                                <ChevronLeft className="h-4 w-4" />
                                <span className="sr-only">Previous</span>
                            </Button>
                            <div className="text-sm text-muted-foreground">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage >= pagination.totalPages}
                            >
                                <ChevronRight className="h-4 w-4" />
                                <span className="sr-only">Next</span>
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        )
    }

    return Content
}
